import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  LinearProgress,
  Chip,
  Button,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Skeleton,
  Divider,
  Paper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  LocalFireDepartment as StreakIcon,
  EmojiEvents as TrophyIcon,
  School as CourseIcon,
  TrendingUp as PointsIcon,
  ArrowForward as ArrowIcon,
  PlayArrow as ContinueIcon,
  Star as BadgeIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { userApi, courseApi, MyCourse, Badge } from '../services/api';
import PointsAnimation from '../components/PointsAnimation';

function DashboardPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Fetch progress summary
  const { data: progress, isLoading: progressLoading } = useQuery({
    queryKey: ['progress-summary'],
    queryFn: () => userApi.getProgressSummary().then((r) => r.data),
  });

  // Fetch recommendations
  const { data: recommendations } = useQuery({
    queryKey: ['recommendations'],
    queryFn: () => userApi.getRecommendations(3).then((r) => r.data.recommendations),
  });

  // Fetch active courses (in progress)
  const { data: myCoursesData } = useQuery({
    queryKey: ['my-courses'],
    queryFn: () => courseApi.getMyCourses().then((r) => r.data.data),
  });

  // Filter to get active (in-progress) courses
  const activeCourses = (myCoursesData || [])
    .filter((c: MyCourse) => c.enrollment?.status === 'in_progress' || c.enrollment?.status === 'enrolled')
    .sort((a: MyCourse, b: MyCourse) =>
      (b.enrollment?.progressPercent || 0) - (a.enrollment?.progressPercent || 0)
    )
    .slice(0, 3);

  // Fetch user's badges
  const { data: badges } = useQuery({
    queryKey: ['user-badges'],
    queryFn: () => userApi.getBadges().then((r) => r.data),
  });

  // Get recently earned badges (last 5)
  const recentBadges = (badges || [])
    .filter((b: Badge) => b.earned)
    .sort((a: Badge, b: Badge) =>
      new Date(b.earnedAt || 0).getTime() - new Date(a.earnedAt || 0).getTime()
    )
    .slice(0, 5);

  // Record activity on mount
  const activityMutation = useMutation({
    mutationFn: () => userApi.recordActivity(),
  });

  useEffect(() => {
    activityMutation.mutate();
  }, []);

  if (progressLoading) {
    return (
      <Box>
        <Skeleton variant="text" width={300} height={48} />
        <Grid container spacing={3} sx={{ mt: 2 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t('dashboard.welcomeName', { name: user?.name || user?.username || t('dashboard.defaultUserName') })}
      </Typography>

      {/* Stats Cards - horizontal scroll on mobile */}
      {isMobile ? (
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            overflowX: 'auto',
            pb: 2,
            mb: 3,
            mx: -2,
            px: 2,
            scrollSnapType: 'x mandatory',
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
          }}
        >
          {/* Points Card - Mobile */}
          <Card sx={{ minWidth: 160, flexShrink: 0, scrollSnapAlign: 'start' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box display="flex" alignItems="center" gap={1.5}>
                <Avatar sx={{ bgcolor: 'primary.light', width: 40, height: 40 }}>
                  <PointsIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight="bold">
                    {progress?.totalPoints || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('common.points')} · {t('dashboard.levelShort')}{progress?.level}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Streak Card - Mobile */}
          <Card sx={{ minWidth: 140, flexShrink: 0, scrollSnapAlign: 'start' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box display="flex" alignItems="center" gap={1.5}>
                <Avatar sx={{ bgcolor: progress?.isActiveToday ? 'warning.main' : 'grey.400', width: 40, height: 40 }}>
                  <StreakIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight="bold">
                    {progress?.currentStreak || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('dashboard.daysStreakShort')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Courses Card - Mobile */}
          <Card sx={{ minWidth: 140, flexShrink: 0, scrollSnapAlign: 'start' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box display="flex" alignItems="center" gap={1.5}>
                <Avatar sx={{ bgcolor: 'success.main', width: 40, height: 40 }}>
                  <CourseIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight="bold">
                    {progress?.completedCourses || 0}/{progress?.totalCourses || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('dashboard.coursesDoneShort')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Leaderboard Card - Mobile */}
          <Card
            sx={{ minWidth: 140, flexShrink: 0, cursor: 'pointer', scrollSnapAlign: 'start' }}
            onClick={() => navigate('/leaderboard')}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box display="flex" alignItems="center" gap={1.5}>
                <Avatar sx={{ bgcolor: 'warning.light', width: 40, height: 40 }}>
                  <TrophyIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="body1" fontWeight="bold">
                    {t('nav.leaderboard')}
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      {t('dashboard.view')}
                    </Typography>
                    <ArrowIcon fontSize="small" color="action" />
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      ) : (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      {t('common.points')}
                    </Typography>
                    <Typography variant="h4">{progress?.totalPoints || 0}</Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'primary.light' }}>
                    <PointsIcon />
                  </Avatar>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {t('dashboard.levelLine', { level: progress?.level, levelName: progress?.levelName })}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      {t('dashboard.streakLabel')}
                    </Typography>
                    <Typography variant="h4">
                      {progress?.currentStreak || 0}
                      <Typography component="span" variant="body1" color="text.secondary">
                        {' '}{t('dashboard.daysUnit')}
                      </Typography>
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: progress?.isActiveToday ? 'warning.main' : 'grey.400' }}>
                    <StreakIcon />
                  </Avatar>
                </Box>
                {progress?.nextMilestone && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {t('dashboard.nextGoalDays', { target: progress.nextMilestone.target })}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      {t('nav.courses')}
                    </Typography>
                    <Typography variant="h4">
                      {progress?.completedCourses || 0}
                      <Typography component="span" variant="body1" color="text.secondary">
                        /{progress?.totalCourses || 0}
                      </Typography>
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'success.main' }}>
                    <CourseIcon />
                  </Avatar>
                </Box>
                <Box sx={{ mt: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={
                      progress?.totalCourses
                        ? (progress.completedCourses / progress.totalCourses) * 100
                        : 0
                    }
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ cursor: 'pointer' }} onClick={() => navigate('/leaderboard')}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      {t('nav.leaderboard')}
                    </Typography>
                    <Typography variant="h4">
                      <TrophyIcon sx={{ color: 'warning.main', mr: 1 }} />
                    </Typography>
                  </Box>
                  <ArrowIcon color="action" />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {t('dashboard.compareProgress')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Next Milestone */}
      {progress?.nextMilestone && (
        <Card sx={{ mb: 4, bgcolor: 'primary.main', color: 'white' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('dashboard.nextMilestone')}
            </Typography>
            <Typography variant="body1">
              {t('dashboard.reachTarget', {
                target: progress.nextMilestone.target,
                type: progress.nextMilestone.type === 'streak' ? t('dashboard.streakType') : t('dashboard.coursesType'),
              })}
            </Typography>
            <Chip
              label={progress.nextMilestone.reward}
              sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
            />
          </CardContent>
        </Card>
      )}

      {/* Active Courses - Continue Learning */}
      {activeCourses.length > 0 && (
        <>
          <Typography variant="h5" gutterBottom>
            {t('dashboard.continueLearning')}
          </Typography>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {activeCourses.map((course: MyCourse) => (
              <Grid item xs={12} md={4} key={course.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  {course.thumbnailUrl ? (
                    <CardMedia
                      component="img"
                      height="100"
                      image={course.thumbnailUrl}
                      alt={course.title}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: 100,
                        bgcolor: 'primary.light',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CourseIcon sx={{ fontSize: 48, color: 'white' }} />
                    </Box>
                  )}
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" noWrap title={course.title}>
                      {course.title}
                    </Typography>
                    <Box sx={{ mt: 1, mb: 2 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                        <Typography variant="body2" color="text.secondary">
                          {t('dashboard.progress')}
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {course.enrollment?.progressPercent || 0}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={course.enrollment?.progressPercent || 0}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      {course.workArea && (
                        <Chip size="small" label={course.workArea.name} variant="outlined" />
                      )}
                      <Chip size="small" label={`${course.pointsReward} P`} color="primary" />
                    </Box>
                  </CardContent>
                  <Box sx={{ p: 2, pt: 0 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<ContinueIcon />}
                      onClick={() => navigate(`/courses/${course.slug}`)}
                    >
                      {t('dashboard.continueButton')}
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Recently Earned Badges - horizontal scroll on mobile */}
      {recentBadges.length > 0 && (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant={isMobile ? 'h6' : 'h5'}>
              {t('dashboard.recentBadges')}
            </Typography>
            <Button
              size="small"
              endIcon={<ArrowIcon />}
              onClick={() => navigate('/badges')}
              sx={{ minHeight: isMobile ? 44 : 'auto' }}
            >
              {t('dashboard.all')}
            </Button>
          </Box>
          <Paper
            sx={{
              mb: 4,
              p: isMobile ? 1 : 2,
              mx: isMobile ? -2 : 0,
              borderRadius: isMobile ? 0 : 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: isMobile ? 'flex-start' : 'flex-start',
                flexWrap: isMobile ? 'nowrap' : 'wrap',
                overflowX: isMobile ? 'auto' : 'visible',
                px: isMobile ? 1 : 0,
                pb: isMobile ? 1 : 0,
                scrollSnapType: isMobile ? 'x mandatory' : 'none',
                '&::-webkit-scrollbar': { display: 'none' },
                scrollbarWidth: 'none',
              }}
            >
              {recentBadges.map((badge: Badge) => (
                <Box
                  key={badge.id}
                  sx={{
                    textAlign: 'center',
                    p: 1,
                    borderRadius: 2,
                    bgcolor: 'background.default',
                    minWidth: isMobile ? 90 : 100,
                    flexShrink: 0,
                    scrollSnapAlign: 'start',
                  }}
                >
                  <Avatar
                    src={badge.imageUrl || undefined}
                    sx={{
                      width: isMobile ? 52 : 64,
                      height: isMobile ? 52 : 64,
                      mx: 'auto',
                      mb: 1,
                      bgcolor: 'warning.light',
                    }}
                  >
                    {!badge.imageUrl && <BadgeIcon sx={{ fontSize: isMobile ? 24 : 32 }} />}
                  </Avatar>
                  <Typography variant="body2" fontWeight="medium" noWrap sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                    {badge.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {badge.earnedAt
                      ? new Date(badge.earnedAt).toLocaleDateString('de-DE')
                      : ''}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </>
      )}

      {/* Recommended Courses */}
      <Typography variant="h5" gutterBottom>
        {t('dashboard.recommendedCourses')}
      </Typography>
      {recommendations && recommendations.length > 0 ? (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {recommendations.map((course) => (
            <Grid item xs={12} md={4} key={course.id}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {course.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {course.description?.substring(0, 100)}
                    {course.description && course.description.length > 100 ? '...' : ''}
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                    <Chip size="small" label={course.level} />
                    <Chip size="small" label={t('dashboard.pointsChip', { points: course.points })} color="primary" />
                    {course.estimatedDuration && (
                      <Chip size="small" label={t('dashboard.minutesChip', { minutes: course.estimatedDuration })} variant="outlined" />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {course.reason}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography color="text.secondary">
              {t('dashboard.noRecommendations')}
            </Typography>
            <Button
              variant="contained"
              sx={{ mt: 2 }}
              onClick={() => navigate('/courses')}
            >
              {t('dashboard.viewAllCourses')}
            </Button>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

export default DashboardPage;
